import React, { useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Grid,
  Alert,
  Button,
  IconButton,
} from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ListAltIcon from "@mui/icons-material/ListAlt";
import CloseIcon from "@mui/icons-material/Close";
import { useFlows, FlowsSubView } from "../../../../stores/flowsStore";

interface FlowMenuCard {
  id: FlowsSubView;
  title: string;
  description: string;
  icon: React.ReactNode;
}

const menuCards: FlowMenuCard[] = [
  {
    id: "transactions",
    title: "Transactions",
    description: "Bulk entry for Trades, FX Spot, and Wire Transfers",
    icon: <SwapHorizIcon sx={{ fontSize: 48, color: "primary.main" }} />,
  },
  {
    id: "automation-flow",
    title: "Automation Flow",
    description: "Create IF/THEN automation rules with email triggers",
    icon: <AccountTreeIcon sx={{ fontSize: 48, color: "primary.main" }} />,
  },
  {
    id: "flows-manager",
    title: "Flows Manager",
    description: "Manage and edit all your automation flows",
    icon: <ListAltIcon sx={{ fontSize: 48, color: "primary.main" }} />,
  },
];

export const FlowsMenu: React.FC = () => {
  const {
    setSubView,
    draftFlowsCount,
    isDraftBannerDismissed,
    dismissDraftBanner,
    resumeDrafts,
    fetchFlows,
  } = useFlows();

  useEffect(() => {
    fetchFlows();
  }, [fetchFlows]);

  const showDraftBanner = draftFlowsCount > 0 && !isDraftBannerDismissed;

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", py: 4 }}>
      {/* Draft Flows Alert Banner */}
      {showDraftBanner && (
        <Alert
          severity="warning"
          sx={{
            mb: 3,
            alignItems: "center",
            "& .MuiAlert-message": {
              display: "flex",
              alignItems: "center",
              width: "100%",
              justifyContent: "space-between",
            },
          }}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={dismissDraftBanner}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography>
              You have {draftFlowsCount} unfinished flow{draftFlowsCount > 1 ? "s" : ""}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              color="warning"
              onClick={resumeDrafts}
            >
              Resume Drafts
            </Button>
          </Box>
        </Alert>
      )}

      <Typography variant="h5" gutterBottom sx={{ mb: 4, textAlign: "center" }}>
        Flows & Automation Tools
      </Typography>

      <Grid container spacing={3} justifyContent="center">
        {menuCards.map((card) => (
          <Grid item xs={12} sm={6} md={4} key={card.id}>
            <Card
              sx={{
                height: "100%",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: 4,
                },
              }}
            >
              <CardActionArea
                onClick={() => setSubView(card.id)}
                sx={{ height: "100%", p: 2 }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    gap: 2,
                  }}
                >
                  {card.icon}
                  <Typography variant="h6" component="div">
                    {card.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {card.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
